import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../config/api';
import ResetCodeButton from '../components/ResetCodeButton';
import SchoolStatsOverview from '../components/SchoolStatsOverview';

const PERMISSION_LABELS = {
  'SUPER_ADMIN/SUPPORT': 'Super Admin',
  'ADMIN': 'Admin',
  'TEACHER': 'Teacher',
};

export default function SchoolDetail({ token, navigate, schoolId }) {
  const [school, setSchool] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [schoolRes, staffRes] = await Promise.all([
        apiFetch(`/schools/${schoolId}`, {}, token),
        apiFetch(`/staff?school_id=${schoolId}`, {}, token),
      ]);
      setSchool(schoolRes.data);
      setStaff(staffRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => { load(); }, [load]);

  const handleDeleteStaff = async (staffId, name) => {
    if (!window.confirm(`Deactivate staff member "${name}"?`)) return;
    try {
      await apiFetch(`/staff/${staffId}`, { method: 'DELETE' }, token);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="sa-empty">Loading...</div>;
  if (!school) return <div className="sa-error">School not found</div>;

  return (
    <div>
      <div className="sa-breadcrumb">
        <button onClick={() => navigate('dashboard')}>Schools</button>
        {' / '}
        {school.legal_name}
      </div>

      {error && <div className="sa-error">{error}</div>}

      <SchoolStatsOverview token={token} schoolId={schoolId} />

      {/* School Info Card */}
      <div className="sa-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ marginBottom: 4 }}>{school.legal_name}</h2>
            <p style={{ color: '#666', fontSize: 14 }}>{school.school_nickname}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="sa-btn sa-btn-primary" onClick={() => navigate('school-edit', school)}>
              Edit School
            </button>
            <button className="sa-btn sa-btn-secondary" onClick={() => navigate('staff', schoolId)}>
              View Staff
            </button>
            <button className="sa-btn sa-btn-secondary" onClick={() => navigate('students', schoolId)}>
              View Students
            </button>
            <button className="sa-btn sa-btn-secondary" onClick={() => navigate('parents', schoolId)}>
              View Parents
            </button>
            <button className="sa-btn sa-btn-secondary" onClick={() => navigate('drivers', schoolId)}>
              View Drivers
            </button>
          </div>
        </div>

        <div className="sa-form-grid" style={{ marginTop: 20 }}>
          <InfoItem label="Type" value={school.type?.replace(/_/g, ' ')} />
          <InfoItem label="Status" value={school.is_active} />
          <InfoItem label="Address" value={`${school.street_address}, ${school.city}, ${school.state} ${school.zip_code}`} />
          <InfoItem label="Phone" value={school.main_phone} />
          <InfoItem label="Email" value={school.main_email} />
          <InfoItem label="Website" value={school.website_url} />
          <InfoItem label="Time Zone" value={school.time_zone} />
          <InfoItem label="Grades" value={`${school.grade_min} to ${school.grade_max}`} />
          <InfoItem label="Est. Students" value={school.estimated_student_body} />
          <InfoItem label="Est. Staff" value={school.estimated_student_staff} />
          <InfoItem label="Billing" value={school.billing_status} />
          <InfoItem label="Payment" value={school.payment_interval} />
        </div>
      </div>

      {/* Staff Section */}
      <div className="sa-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ marginBottom: 0 }}>Staff ({staff.length})</h2>
          <button className="sa-btn sa-btn-primary" onClick={() => navigate('staff-create', schoolId)}>
            + Add Staff
          </button>
        </div>

        {staff.length === 0 ? (
          <div className="sa-empty">No staff members yet. Add the first staff member for this school.</div>
        ) : (
          <table className="sa-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Position</th>
                <th>Permission</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600 }}>{s.f_name} {s.l_name}</td>
                  <td>{s.school_email || '—'}</td>
                  <td>{s.mobile_phone}</td>
                  <td>{s.position_title || '—'}</td>
                  <td>
                    <span className="sa-badge sa-badge-active">
                      {PERMISSION_LABELS[s.permission_level] || s.permission_level}
                    </span>
                  </td>
                  <td>{s.is_active ? 'Yes' : 'No'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        className="sa-btn sa-btn-secondary"
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => navigate('staff-edit', { staff: s, schoolId })}
                      >
                        Edit
                      </button>
                      <ResetCodeButton token={token} userType="staff" userId={s.id} userName={`${s.f_name} ${s.l_name}`} />
                      <button
                        className="sa-btn sa-btn-danger"
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => handleDeleteStaff(s.id, `${s.f_name} ${s.l_name}`)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 14, marginTop: 2 }}>{value || '—'}</div>
    </div>
  );
}
